import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixAgentsV1AgentRegistryServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {};

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_agents_agent-registry';

/** Creates an AgentDefinition. */
export function createAgentDefinition(
  payload: object
): RequestOptionsFactory<any> {
  function __createAgentDefinition({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'agentDefinition.createdDate' },
          { path: 'agentDefinition.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.agents.v1.agent_definition',
      method: 'POST' as any,
      methodFqn: 'wix.agents.v1.AgentRegistryService.CreateAgentDefinition',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixAgentsV1AgentRegistryServiceUrl({
        protoPath: '/v1/agent-definitions',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'agentDefinition.createdDate' },
              { path: 'agentDefinition.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createAgentDefinition;
}

/** Retrieves an AgentDefinition. */
export function getAgentDefinition(
  payload: object
): RequestOptionsFactory<any> {
  function __getAgentDefinition({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.agents.v1.agent_definition',
      method: 'GET' as any,
      methodFqn: 'wix.agents.v1.AgentRegistryService.GetAgentDefinition',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixAgentsV1AgentRegistryServiceUrl({
        protoPath: '/v1/agent-definitions/{agentDefinitionId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'agentDefinition.createdDate' },
              { path: 'agentDefinition.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getAgentDefinition;
}

/** Updates an AgentDefinition. */
export function updateAgentDefinition(
  payload: object
): RequestOptionsFactory<any> {
  function __updateAgentDefinition({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'agentDefinition.createdDate' },
          { path: 'agentDefinition.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.agents.v1.agent_definition',
      method: 'PATCH' as any,
      methodFqn: 'wix.agents.v1.AgentRegistryService.UpdateAgentDefinition',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixAgentsV1AgentRegistryServiceUrl({
        protoPath: '/v1/agent-definitions/{agentDefinition.id}',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'agentDefinition.createdDate' },
              { path: 'agentDefinition.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateAgentDefinition;
}

/** Deletes an AgentDefinition. */
export function deleteAgentDefinition(
  payload: object
): RequestOptionsFactory<any> {
  function __deleteAgentDefinition({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.agents.v1.agent_definition',
      method: 'DELETE' as any,
      methodFqn: 'wix.agents.v1.AgentRegistryService.DeleteAgentDefinition',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixAgentsV1AgentRegistryServiceUrl({
        protoPath: '/v1/agent-definitions/{agentDefinitionId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteAgentDefinition;
}

/** Retrieves a list of AgentDefinitions by WQL query. */
export function queryAgentDefinitions(
  payload: object
): RequestOptionsFactory<any> {
  function __queryAgentDefinitions({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.agents.v1.agent_definition',
      method: 'GET' as any,
      methodFqn: 'wix.agents.v1.AgentRegistryService.QueryAgentDefinitions',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixAgentsV1AgentRegistryServiceUrl({
        protoPath: '/v1/agent-definitions/query',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload, true),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'agentDefinitions.createdDate' },
              { path: 'agentDefinitions.updatedDate' },
            ],
          },
        ]),
      fallback: [
        {
          method: 'POST' as any,
          url: resolveWixAgentsV1AgentRegistryServiceUrl({
            protoPath: '/v1/agent-definitions/query',
            data: payload,
            host,
          }),
          data: payload,
        },
      ],
    };

    return metadata;
  }

  return __queryAgentDefinitions;
}

/** Synchronously updates tags on multiple AgentDefinitions by ID. */
export function bulkUpdateAgentDefinitionTags(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpdateAgentDefinitionTags({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.agents.v1.agent_definition',
      method: 'POST' as any,
      methodFqn:
        'wix.agents.v1.AgentRegistryService.BulkUpdateAgentDefinitionTags',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixAgentsV1AgentRegistryServiceUrl({
        protoPath: '/v1/bulk/agent-definitions/update-tags',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkUpdateAgentDefinitionTags;
}

/** Asynchronously updates tags on AgentDefinitions matching the provided filter. */
export function bulkUpdateAgentDefinitionTagsByFilter(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpdateAgentDefinitionTagsByFilter({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.agents.v1.agent_definition',
      method: 'POST' as any,
      methodFqn:
        'wix.agents.v1.AgentRegistryService.BulkUpdateAgentDefinitionTagsByFilter',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixAgentsV1AgentRegistryServiceUrl({
        protoPath: '/v1/bulk/agent-definitions/update-tags-by-filter',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkUpdateAgentDefinitionTagsByFilter;
}
